import { call, cancelled, takeEvery, takeLatest } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import { actions } from '@restore/SupportSubject/SupportSubject.slice';
import { dispatch, Resp } from '@restore/helpers';
import {
  SupportSubjectQCTasksFetchAction,
  SupportSubjectReadingTasksFetchAction,
  SupportSubjectRemoveUploadAction,
  SupportSubjectsListFetchAction,
  SupportSubjectTaskDefinitionsFetchAction,
  SupportSubjectUploadsFetchAction,
  SupportSubjectVisitDatesFetchAction,
} from '@restore/SupportSubject/SupportSubject.types';
import * as api from '@api/supportSubjects';
import {
  RemoveUploadValidationResponse,
  ReopenTaskValidationResponse,
} from '@dynamika-shared/types/subjects';
import { runToast, showErrorToast } from '@restore/tools';

const reopenValidationAbortControllers = new Map<string, AbortController>();

const isAbortError = (error: unknown) => {
  if (!(error instanceof Error)) return false;

  return (
    error.name === 'AbortError' ||
    error.name === 'CanceledError' ||
    (error as Error & { code?: string }).code === 'ERR_CANCELED'
  );
};

export function* fetchSupportSubjectsListWorker(
  action: SupportSubjectsListFetchAction
) {
  try {
    const { clinicalTrialId, args } = action.payload;

    const response: Resp<typeof api.fetchSubjectsByCT> = yield call(
      api.fetchSubjectsByCT,
      clinicalTrialId,
      args,
      args?.patientCode
    );

    yield dispatch(actions.fetchSupportSubjectsListSuccess(response));
  } catch (error) {
    yield dispatch(actions.fetchSupportSubjectsListFailure(error as Error));
  }
}

export function* fetchSupportSubjectUploadsWorker(
  action: SupportSubjectUploadsFetchAction
) {
  try {
    const { clinicalTrialId, patientId } = action.payload;

    const data: ReturnType<typeof api.fetchUploadsBySubject> extends Promise<
      infer R
    >
      ? R
      : never = yield call(
      api.fetchUploadsBySubject,
      clinicalTrialId,
      patientId
    );

    const key = `${clinicalTrialId}:${patientId}`;
    yield dispatch(actions.fetchSupportSubjectUploadsSuccess({ key, data }));
  } catch (error) {
    yield dispatch(actions.fetchSupportSubjectUploadsFailure(error as Error));
  }
}

export function* fetchSupportSubjectTaskDefinitionsWorker(
  action: SupportSubjectTaskDefinitionsFetchAction
) {
  try {
    const { clinicalTrialId, patientId } = action.payload;

    const data: ReturnType<
      typeof api.fetchTaskDefinitionsBySubject
    > extends Promise<infer R>
      ? R
      : never = yield call(
      api.fetchTaskDefinitionsBySubject,
      clinicalTrialId,
      patientId
    );

    const key = `${clinicalTrialId}:${patientId}`;
    yield dispatch(
      actions.fetchSupportSubjectTaskDefinitionsSuccess({ key, data })
    );
  } catch (error) {
    yield dispatch(
      actions.fetchSupportSubjectTaskDefinitionsFailure(error as Error)
    );
  }
}

export function* fetchSupportSubjectReadingTasksWorker(
  action: SupportSubjectReadingTasksFetchAction
) {
  try {
    const { clinicalTrialId, patientId } = action.payload;

    const data: ReturnType<
      typeof api.fetchReadingTasksBySubject
    > extends Promise<infer R>
      ? R
      : never = yield call(
      api.fetchReadingTasksBySubject,
      clinicalTrialId,
      patientId
    );

    const key = `${clinicalTrialId}:${patientId}`;
    yield dispatch(
      actions.fetchSupportSubjectReadingTasksSuccess({ key, data })
    );
  } catch (error) {
    yield dispatch(
      actions.fetchSupportSubjectReadingTasksFailure(error as Error)
    );
  }
}

export function* fetchSupportSubjectQCTasksWorker(
  action: SupportSubjectQCTasksFetchAction
) {
  try {
    const { clinicalTrialId, patientId } = action.payload;

    const data: ReturnType<typeof api.fetchQCTasksBySubject> extends Promise<
      infer R
    >
      ? R
      : never = yield call(
      api.fetchQCTasksBySubject,
      clinicalTrialId,
      patientId
    );

    const key = `${clinicalTrialId}:${patientId}`;
    yield dispatch(actions.fetchSupportSubjectQCTasksSuccess({ key, data }));
  } catch (error) {
    yield dispatch(actions.fetchSupportSubjectQCTasksFailure(error as Error));
  }
}

export function* fetchSupportSubjectVisitDatesWorker(
  action: SupportSubjectVisitDatesFetchAction
) {
  try {
    const { clinicalTrialId, patientId } = action.payload;

    const data: ReturnType<typeof api.fetchVisitDates> extends Promise<infer R>
      ? R
      : never = yield call(api.fetchVisitDates, clinicalTrialId, patientId);

    const key = `${clinicalTrialId}:${patientId}`;
    yield dispatch(actions.fetchSupportSubjectVisitDatesSuccess({ key, data }));
  } catch (error) {
    yield dispatch(
      actions.fetchSupportSubjectVisitDatesFailure(error as Error)
    );
  }
}

export function* removeSupportSubjectUploadWorker(
  action: SupportSubjectRemoveUploadAction
) {
  const customErrorMessage = 'Failed to remove upload item';

  try {
    const { clinicalTrialId, key, uploadId, ticket, comment } = action.payload;

    const body = comment ? { ticket, comment } : { ticket };

    const validation: RemoveUploadValidationResponse = yield call(
      api.validateRemoveUpload,
      clinicalTrialId,
      uploadId
    );

    const validationFailed =
      validation?.status === 'FAILED' ||
      (validation?.errors && validation.errors.length > 0);

    if (validationFailed) {
      const errorMsg =
        validation.errors?.[0]?.error ??
        'Unable to remove upload due to validation error.';

      const error = new Error(errorMsg);

      yield call(runToast, 'error', `${customErrorMessage} - ${errorMsg}`);

      yield dispatch(
        actions.removeSupportSubjectUploadFailure({
          uploadId,
          error,
        })
      );

      return;
    }

    yield call(api.removeUpload, clinicalTrialId, uploadId, body);

    yield dispatch(
      actions.removeSupportSubjectUploadSuccess({
        key,
        uploadId,
      })
    );

    yield call(runToast, 'success', 'Upload removed successfully');
  } catch (error) {
    yield dispatch(
      actions.removeSupportSubjectUploadFailure({
        uploadId: action.payload.uploadId,
        error: error as Error,
      })
    );

    yield call(showErrorToast, error as Error, customErrorMessage);
  }
}

const getReopenValidationErrors = (
  validation?: ReopenTaskValidationResponse
) => {
  const hasErrorEntries =
    Array.isArray(validation?.errors) && validation.errors.length > 0;
  const errors =
    validation?.errors
      ?.map(item => item?.error)
      .filter((error): error is string => Boolean(error)) ?? [];

  if (
    (validation?.status === 'FAILED' || hasErrorEntries) &&
    errors.length === 0
  ) {
    return ['Unable to reopen IS Task due to validation error.'];
  }

  return errors;
};

export function* validateReopenSupportSubjectTaskWorker(
  action: ReturnType<typeof actions.validateReopenSupportSubjectTask>
): SagaIterator {
  const customErrorMessage = 'Failed to validate reopen IS Task';
  const { clinicalTrialId, taskId } = action.payload;
  const previousController = reopenValidationAbortControllers.get(taskId);

  previousController?.abort();

  const controller = new AbortController();
  reopenValidationAbortControllers.set(taskId, controller);

  try {
    const validation: ReopenTaskValidationResponse = yield call(
      api.validateReopenVisit,
      clinicalTrialId,
      taskId,
      controller.signal
    );

    yield dispatch(
      actions.validateReopenSupportSubjectTaskSuccess({
        taskId,
        errors: getReopenValidationErrors(validation),
      })
    );
  } catch (error) {
    if (isAbortError(error)) return;

    yield dispatch(
      actions.validateReopenSupportSubjectTaskFailure({
        taskId,
        error: error as Error,
      })
    );

    yield call(showErrorToast, error as Error, customErrorMessage);
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }

    if (reopenValidationAbortControllers.get(taskId) === controller) {
      reopenValidationAbortControllers.delete(taskId);
    }
  }
}

export function* cancelReopenSupportSubjectTaskValidationWorker(
  action: ReturnType<typeof actions.clearReopenSupportSubjectTaskValidation>
): SagaIterator {
  const controller = reopenValidationAbortControllers.get(action.payload.taskId);

  if (!controller) return;

  yield call([controller, controller.abort]);
  reopenValidationAbortControllers.delete(action.payload.taskId);
}

export function* reopenSupportSubjectTaskWorker(
  action: ReturnType<typeof actions.reopenSupportSubjectTask>
) {
  const customErrorMessage = 'Failed to reopen IS Task';

  try {
    const { clinicalTrialId, patientId, taskId, ticket, comment } =
      action.payload;

    const body = { ticket, comment };

    const result: {
      id: string;
      status: string;
      details?: unknown[];
      errors?: { error?: string }[];
    } = yield call(api.reopenTask, clinicalTrialId, taskId, body);

    if (result?.status !== 'SUCCESS') {
      const errorMsg =
        result?.errors?.[0]?.error ??
        'Unable to reopen IS Task, server returned failure.';

      const error = new Error(errorMsg);

      yield dispatch(
        actions.reopenSupportSubjectTaskFailure({
          taskId,
          error,
        })
      );

      yield call(runToast, 'error', `${customErrorMessage} - ${errorMsg}`);
      return;
    }

    yield dispatch(
      actions.reopenSupportSubjectTaskSuccess({
        taskId,
      })
    );

    if (clinicalTrialId && patientId) {
      yield dispatch(
        actions.fetchSupportSubjectVisitDates({ clinicalTrialId, patientId })
      );
      yield dispatch(
        actions.fetchSupportSubjectUploads({ clinicalTrialId, patientId })
      );
      yield dispatch(
        actions.fetchSupportSubjectTaskDefinitions({
          clinicalTrialId,
          patientId,
        })
      );
      yield dispatch(
        actions.fetchSupportSubjectReadingTasks({ clinicalTrialId, patientId })
      );
      yield dispatch(
        actions.fetchSupportSubjectQCTasks({ clinicalTrialId, patientId })
      );
    }

    yield call(runToast, 'success', 'IS Task reopened successfully');
  } catch (error) {
    yield dispatch(
      actions.reopenSupportSubjectTaskFailure({
        taskId: action.payload.taskId,
        error: error as Error,
      })
    );

    yield call(showErrorToast, error as Error, customErrorMessage);
  }
}

export function* SupportSubjectSaga() {
  yield takeLatest(
    actions.fetchSupportSubjectsList.type,
    fetchSupportSubjectsListWorker
  );
  yield takeLatest(
    actions.fetchSupportSubjectUploads.type,
    fetchSupportSubjectUploadsWorker
  );
  yield takeLatest(
    actions.fetchSupportSubjectTaskDefinitions.type,
    fetchSupportSubjectTaskDefinitionsWorker
  );
  yield takeLatest(
    actions.fetchSupportSubjectReadingTasks.type,
    fetchSupportSubjectReadingTasksWorker
  );
  yield takeLatest(
    actions.fetchSupportSubjectQCTasks.type,
    fetchSupportSubjectQCTasksWorker
  );
  yield takeLatest(
    actions.fetchSupportSubjectVisitDates.type,
    fetchSupportSubjectVisitDatesWorker
  );
  yield takeLatest(
    actions.removeSupportSubjectUpload.type,
    removeSupportSubjectUploadWorker
  );
  yield takeLatest(
    actions.validateReopenSupportSubjectTask.type,
    validateReopenSupportSubjectTaskWorker
  );
  yield takeEvery(
    actions.clearReopenSupportSubjectTaskValidation.type,
    cancelReopenSupportSubjectTaskValidationWorker
  );
  yield takeLatest(
    actions.reopenSupportSubjectTask.type,
    reopenSupportSubjectTaskWorker
  );
}
